import React from "react";
import { Link } from "react-router-dom";
import "../../ComponentsCss/PrivacyPolicy/PrivacyPolicy.css";

const informationCollected = [
  {
    label: "Account Information",
    description:
      "Name, email address, and other account details provided during registration or login, where applicable.",
  },
  {
    label: "Guest Account Information",
    description: "Information associated with guest access and the guest session.",
  },
  {
    label: "User-Generated Content",
    description:
      "Blog posts, comments, likes, images, and other content you choose to submit through the application.",
  },
  {
    label: "Location Data",
    description:
      "Location information, where permission is granted and where required for supported application features, such as location-based weather information.",
  },
  {
    label: "Activity Information",
    description:
      "Daily step counts or related activity information, if enabled and available through the application's supported data sources.",
  },
  {
    label: "Device Information",
    description:
      "Device model, operating system version, application version, and related technical information.",
  },
  {
    label: "Application Usage Information",
    description:
      "Information about interactions with application features, where collected.",
  },
  {
    label: "Diagnostic Information",
    description:
      "Error reports, crash logs, and other technical data used to identify and resolve application issues.",
  },
];

const informationUses = [
  "Provide account registration, login, and guest access.",
  "Display blogs and other content available through the application.",
  "Allow users to create and publish blog posts.",
  "Support likes, comments, and other available content interactions.",
  "Provide weather information and related location-based features, where applicable.",
  "Display daily step counts and activity information, where supported.",
  "Maintain application functionality and improve performance.",
  "Troubleshoot technical issues and diagnose errors.",
  "Protect accounts, prevent misuse, and maintain application security.",
  "Respond to support requests and comply with applicable legal obligations.",
];

const permissions = [
  {
    permission: "Location",
    purpose:
      "Support location-dependent features, such as local weather information, where applicable.",
  },
  {
    permission: "Internet",
    purpose:
      "Communicate with application servers and retrieve content and information.",
  },
  {
    permission: "Notifications",
    purpose:
      "Deliver relevant application updates and notifications, if enabled.",
  },
  {
    permission: "Photos or Gallery",
    purpose: "Select images for blog posts, if image uploads are supported.",
  },
  {
    permission: "Camera",
    purpose:
      "Capture images directly within the application, if supported.",
  },
  {
    permission: "Physical Activity or Health Data",
    purpose:
      "Access step counts or activity information, if the application integrates with a supported health or fitness data source.",
  },
];

const userRights = [
  "Access personal information associated with your account.",
  "Correct inaccurate personal information.",
  "Request deletion of your account and associated personal information.",
  "Withdraw device permissions, including location permissions.",
  "Ask questions about how your information is collected and used.",
];

function PrivacyPolicy() {
  return (
    <main className="privacy-page">
      <header className="privacy-header">
        <Link className="privacy-brand" to="/login" aria-label="Manas home">
          <span className="privacy-brand-mark">M</span>
          <span>
            Manas<span> by Xeliumtech</span>
          </span>
        </Link>
        <Link className="privacy-header-link" to="/support">
          Contact support
        </Link>
      </header>

      <article className="privacy-content">
        <div className="privacy-title-block">
          <p className="privacy-eyebrow">MANAS · XELIUMTECH</p>
          <h1>Privacy Policy</h1>
          <p className="privacy-effective-date">
            <strong>Effective Date:</strong> October 23, 2026
          </p>
        </div>

        <div className="privacy-introduction">
          <p>
            Manas is developed and operated by <strong>Xeliumtech</strong>. This
            application is provided as a free service and is intended for use
            as is.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, and
            protect your information when you use the Manas mobile application.
          </p>
          <p>
            By using this application, you acknowledge the practices described
            in this Privacy Policy.
          </p>
        </div>

        <section className="privacy-section" aria-labelledby="information-collected">
          <h2 id="information-collected">Information We Collect</h2>
          <p>
            Depending on how you use the application and the permissions you
            grant, we may collect or process the following information:
          </p>
          <ul className="privacy-detail-list">
            {informationCollected.map((item) => (
              <li key={item.label}>
                <strong>{item.label}:</strong> {item.description}
              </li>
            ))}
          </ul>
          <p>
            The information collected depends on the features you use, the
            permissions you grant, and the application's configuration.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="information-use">
          <h2 id="information-use">How We Use Your Information</h2>
          <p>We may use your information to:</p>
          <ul>
            {informationUses.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="privacy-section" aria-labelledby="location-information">
          <h2 id="location-information">Location Information</h2>
          <p>
            Manas may use location information to support features that depend
            on a user's location, such as providing relevant weather
            information, where applicable.
          </p>
          <p>
            Location access depends on the permissions requested by the
            application and granted through your device settings.
          </p>
          <p>
            We use location information for the purposes described in this
            Privacy Policy and do not sell personal information for advertising
            purposes.
          </p>
          <p>
            You can manage or revoke location permissions through your device
            settings. Some location-dependent features may not work correctly
            if permission is denied.
          </p>
          <p>
            If the application collects location information in the
            background, this policy and the application's permission
            disclosures should specifically explain when and why that
            collection occurs.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="permissions-used">
          <h2 id="permissions-used">Permissions Used</h2>
          <p>
            The application may use the following permissions, depending on
            the features enabled in the released version:
          </p>
          <div className="privacy-table-wrap">
            <table className="privacy-permissions-table">
              <thead>
                <tr>
                  <th scope="col">Permission</th>
                  <th scope="col">Purpose</th>
                </tr>
              </thead>
              <tbody>
                {permissions.map((item) => (
                  <tr key={item.permission}>
                    <th scope="row">{item.permission}</th>
                    <td>{item.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="privacy-note">
            Only permissions actually requested or used by the released
            application should be listed as active permissions.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="third-party">
          <h2 id="third-party">Third-Party Services</h2>
          <p>
            Manas may use third-party services to support application
            functionality, deliver notifications, process health or activity
            information where enabled, and maintain service reliability.
          </p>
          <p>
            Depending on the features enabled in the application, these
            services may include:
          </p>
          <ul>
            <li>
              <strong>Firebase:</strong> Used for application infrastructure
              and related services where configured.
            </li>
            <li>
              <strong>Firebase Cloud Messaging:</strong> Used to deliver push
              notifications where enabled.
            </li>
            <li>
              <strong>Apple HealthKit:</strong> Used to access health or
              activity information, such as step counts, only where the user
              grants permission and the feature is implemented.
            </li>
            <li>
              <strong>Third-party weather or mapping services:</strong> Used to
              provide weather information or mapping functionality, where
              applicable.
            </li>
          </ul>
          <p>
            Third-party services may process information necessary to provide
            their functionality, subject to their respective privacy policies
            and applicable requirements.
          </p>
          <p>For more information, please review the privacy policies of the services actually integrated into Manas:</p>
          <ul>
            <li>
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer">
                Firebase Privacy and Security
              </a>
            </li>
            <li>
              <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noreferrer">
                Apple Privacy
              </a>
            </li>
          </ul>
          <p className="privacy-note">
            Only services actually used by the released application should be
            listed here.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="log-data">
          <h2 id="log-data">Log Data</h2>
          <p>
            If an error occurs while using Manas, technical information may be
            collected to help identify and resolve the issue. This information
            may include:
          </p>
          <ul>
            <li>Device model and operating system version.</li>
            <li>Application version.</li>
            <li>Date and time of the error.</li>
            <li>Crash reports and diagnostic information.</li>
            <li>
              IP address or other technical connection information, where
              collected by the application or its service providers.
            </li>
          </ul>
          <p>
            Log data is used for troubleshooting, maintaining application
            reliability, and improving performance.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="cookies">
          <h2 id="cookies">Cookies and Similar Technologies</h2>
          <p>
            Manas does not directly use cookies unless implemented as part of a
            specific feature. However, integrated third-party services or
            web-based components may use cookies or similar technologies to
            support their functionality.
          </p>
          <p>
            Where applicable, these technologies are governed by the relevant
            service provider's policies.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="user-content">
          <h2 id="user-content">User-Generated Content</h2>
          <p>
            Manas allows users to browse blogs and, where enabled, create
            posts, like content, and submit comments.
          </p>
          <p>
            Content submitted through these features may be stored and
            displayed to other users according to the application's
            functionality and access settings.
          </p>
          <p>
            Users should avoid including sensitive personal information in
            publicly visible posts or comments.
          </p>
          <p>
            We may take appropriate action concerning content that violates
            applicable rules or legal requirements, subject to the
            application's available moderation processes.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="data-sharing">
          <h2 id="data-sharing">Data Sharing</h2>
          <p>We do not sell personal information to third parties.</p>
          <p>Information may be shared or processed where necessary to:</p>
          <ul>
            <li>Operate and maintain the application.</li>
            <li>Provide services through authorized third-party providers.</li>
            <li>Deliver application functionality requested by users.</li>
            <li>Protect the security and integrity of the service.</li>
            <li>
              Comply with applicable laws, legal processes, or valid
              governmental requests.
            </li>
          </ul>
          <p>
            Information shared with service providers should be limited to
            what is necessary for their assigned functions, subject to
            applicable agreements and legal requirements.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="data-retention">
          <h2 id="data-retention">Data Retention</h2>
          <p>
            We retain personal information for as long as reasonably necessary
            to provide the application services, maintain security, resolve
            disputes, comply with legal obligations, and enforce applicable
            agreements.
          </p>
          <p>
            Retention periods may vary depending on the type of information and
            the purpose for which it is collected.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="security">
          <h2 id="security">Security</h2>
          <p>
            We use reasonable technical and organizational measures to help
            protect personal information against unauthorized access, loss,
            misuse, alteration, or disclosure.
          </p>
          <p>
            However, no method of electronic transmission or storage is
            completely secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="your-rights">
          <h2 id="your-rights">Your Rights</h2>
          <p>Subject to applicable law, you may request to:</p>
          <ul>
            {userRights.map((right) => (
              <li key={right}>{right}</li>
            ))}
          </ul>
          <p>
            Withdrawing a device permission may limit the availability of
            features that depend on that permission.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="data-deletion">
          <h2 id="data-deletion">Data Deletion Requests</h2>
          <p>
            You may request deletion of personal information associated with
            your Manas account by contacting:
          </p>
          <p>
            <strong>Email: </strong>
            <a href="mailto:hr@xeliumtech.com">hr@xeliumtech.com</a>
          </p>
          <p>
            Please include sufficient information to help us identify your
            account. Do not include your password or other sensitive
            authentication credentials.
          </p>
          <p>
            We will review and process verified requests in accordance with
            applicable laws and our data-retention obligations.
          </p>
          <p>
            Where deletion is subject to a legal exception or certain
            information must be retained, we will handle that information in
            accordance with applicable requirements.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="childrens-privacy">
          <h2 id="childrens-privacy">Children's Privacy</h2>
          <p>
            Manas is not intended for children under the age of 13, and we do
            not knowingly collect personal information from children under 13.
          </p>
          <p>
            If we become aware that personal information has been collected
            from a child under 13 without appropriate authorization, we will
            take reasonable steps to address the situation in accordance with
            applicable law.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="external-links">
          <h2 id="external-links">Links to Other Websites</h2>
          <p>
            The application may contain links to third-party websites or
            services. If you follow a third-party link, you will be directed to
            a website governed by its own privacy policy.
          </p>
          <p>
            We are not responsible for the privacy practices or content of
            third-party websites. We encourage users to review the privacy
            policies of any external services they visit.
          </p>
        </section>

        <section className="privacy-section" aria-labelledby="policy-changes">
          <h2 id="policy-changes">Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect
            changes in application functionality, data practices, or legal
            requirements.
          </p>
          <p>
            Updates will be published on the applicable privacy policy page
            with a revised Effective Date. We encourage users to review this
            page periodically.
          </p>
        </section>

        <section className="privacy-section privacy-contact" aria-labelledby="contact-us">
          <h2 id="contact-us">Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy, your personal
            information, or a data deletion request, please contact:
          </p>
          <p>
            <strong>Xeliumtech</strong>
          </p>
          <p>
            <strong>Email: </strong>
            <a href="mailto:hr@xeliumtech.com">hr@xeliumtech.com</a>
          </p>
        </section>
      </article>

      <footer className="privacy-footer">
        <span>© 2026 Xeliumtech. All Rights Reserved.</span>
        <Link to="/support">Support</Link>
      </footer>
    </main>
  );
}

export default PrivacyPolicy;
